import React, { useState } from 'react';
import { DocLayout, NavItem } from '../components/docs/DocLayout';
import {
  H2, Lead, Divider, SectionAnchor, Pill,
} from '../components/docs/shared';
import { C } from '../theme';
import { SUPPORT_EMAIL, mailto } from '../config/brand';
import '../styles/patterns.css';

const NAV: NavItem[] = [
  { id: 'rate-limits', label: 'Rate Limits', depth: 0 },
  { id: 'changelog', label: 'Changelog', depth: 0 },
  { id: 'support', label: 'Support', depth: 0 },
];

export const DocsReference: React.FC = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <DocLayout slug="docs/reference" nav={NAV}>

      {/* ══ RATE LIMITS ══════════════════════════════════════════════════ */}
      <SectionAnchor id="rate-limits" />
      <H2 index="01">Rate Limits & Status Codes</H2>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 0, padding: '20px 24px' }}>
          <div style={{ fontFamily: C.mono, fontSize: '0.7rem', color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>Rate Limits</div>
          {[
            { label: 'Per developer key', value: '1,000 req / hour', note: 'sandbox + production combined' },
            { label: 'Per user', value: '5 verifications / hour', note: 'across all developer keys' },
            { label: 'Enterprise', value: 'Custom', note: 'contact sales' },
          ].map(r => (
            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontFamily: C.sans, fontSize: '0.83rem', color: C.text }}>{r.label}</div>
                <div style={{ fontFamily: C.sans, fontSize: '0.72rem', color: C.dim }}>{r.note}</div>
              </div>
              <code style={{ fontFamily: C.mono, fontSize: '0.8rem', color: C.cyan, alignSelf: 'center' }}>{r.value}</code>
            </div>
          ))}
        </div>

        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 0, padding: '20px 24px' }}>
          <div style={{ fontFamily: C.mono, fontSize: '0.7rem', color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>HTTP Status Codes</div>
          {[
            { code: '200 / 201', color: C.green, desc: 'Success' },
            { code: '400', color: C.amber, desc: 'Bad request — validation error' },
            { code: '401', color: C.red, desc: 'Unauthorized — invalid or missing API key' },
            { code: '404', color: C.red, desc: 'Verification not found' },
            { code: '409', color: C.orange, desc: 'Conflict — session hard-rejected by a gate' },
            { code: '429', color: C.orange, desc: 'Rate limit exceeded' },
            { code: '500', color: C.red, desc: 'Internal server error' },
          ].map(s => (
            <div key={s.code} style={{ display: 'flex', gap: 14, padding: '7px 0', borderTop: `1px solid ${C.border}`, alignItems: 'center' }}>
              <code style={{ fontFamily: C.mono, fontSize: '0.78rem', color: s.color, width: 72, flexShrink: 0 }}>{s.code}</code>
              <span style={{ fontFamily: C.sans, fontSize: '0.82rem', color: C.muted }}>{s.desc}</span>
            </div>
          ))}
        </div>
      </div>

      <Divider />

      {/* ══ CHANGELOG ═══════════════════════════════════════════════════ */}
      <SectionAnchor id="changelog" />
      <H2 index="02">Changelog</H2>
      <Lead>
        Notable changes to the Saflens API. This project uses{' '}
        <a href="https://semver.org" target="_blank" rel="noopener noreferrer" style={{ color: C.cyan, textDecoration: 'none' }}>Semantic Versioning</a>.
      </Lead>

      {[
        {
          version: '1.8.3',
          date: '2026-04-09',
          added: [],
          fixed: [
            'Compliance auth recursion bug — authenticateComplianceRequest self-recursed on the X-API-Key branch, which would have stack-overflowed any API key request (latent because the previous UI only ever hit the JWT branch)',
          ],
          changed: [
            'Compliance ruleset UI moved from Developer Portal to Admin Dashboard (Verification Management → Compliance Rules tab, gated to organization admins on cloud edition)',
            'Compliance endpoints now accept exactly two auth paths: X-API-Key (developer SDK/automation) OR organization-admin reviewer session cookie. Regular reviewers and platform admins are rejected',
            'CSRF enforced on /api/v2/compliance routes (no-ops for X-API-Key callers, enforced for cookie-auth)',
          ],
        },
        {
          version: '1.8.2',
          date: '2026-04-02',
          added: [
            'Verification page branding — white-label the hosted verification page with a custom logo, accent color, and company name',
            'GET/PUT /api/developer/settings/branding — configure branding settings',
            'POST /api/developer/branding/logo — upload branding logo (JPEG/PNG, max 2 MB)',
            'GET /api/v2/verify/page-config?api_key=... — public endpoint returning developer branding (cached 5 min)',
            'Live preview panel in Developer Portal Settings modal',
          ],
          fixed: [],
          changed: [],
        },
        {
          version: '1.8.1',
          date: '2026-04-02',
          added: [
            'Custom verification flows — verification_mode now supports document_only (front → back → cross-validation) and identity (front → liveness → face match) presets',
            'Endpoint guards: back-document returns 400 for identity/age_only flows; live-capture returns 400 for document_only/age_only flows',
          ],
          fixed: [],
          changed: [],
        },
        {
          version: '1.8.0',
          date: '2026-04-02',
          added: [
            'Role-based access control — verification_reviewers table now has a role column (admin or reviewer), enabling Organization Admins with elevated privileges',
            'Organization Admin role — access analytics, GDPR data deletion, and override verification decisions scoped to their developer',
            'Role-aware reviewer invitations: POST /api/developer/reviewers/invite accepts optional role parameter',
            'requireOrgAdminOrPlatformAdmin middleware — gates analytics, GDPR delete, and override endpoints',
            'Team setup banner + role badges in Developer Portal Settings',
          ],
          fixed: [],
          changed: [
            'Developer escalation removed — POST /api/auth/admin/escalate now returns 410 Gone',
            'Analytics endpoints opened to org admins (scoped by developer_id)',
            'Verification override restricted to org admins and platform admins (regular reviewers get 403)',
            'GET /api/admin/developers restricted to platform admins only',
          ],
        },
        {
          version: '1.7.2',
          date: '2026-03-30',
          added: [
            'AML screening auto-trigger — runs automatically on all non-sandbox verifications when AML_PROVIDER is configured (no longer requires per-session addons.aml_screening)',
            'Multi-provider AML screening — AML_PROVIDER supports comma-separated providers (e.g. opensanctions,offline), matches deduplicated, highest risk level wins',
            'AML result persistence in aml_screenings table with full match details (listed name, list source, score, match type)',
            'Address cross-validation — front OCR address compared against back barcode address as a supplementary signal',
            'Developer aml_enabled column (opt-out)',
          ],
          fixed: [],
          changed: [
            'Risk scoring weights rebalanced to include aml_screening factor (weight 0.10)',
          ],
        },
        {
          version: '1.7.0',
          date: '2026-03-27',
          added: [
            'Reviewer invitation system — developers invite external reviewers with OTP-based access scoped to their verifications',
            'Reviewer auth: POST /api/auth/reviewer/otp/send, POST /api/auth/reviewer/otp/verify',
            'Reviewer management: POST /api/developer/reviewers/invite, GET /api/developer/reviewers, DELETE /api/developer/reviewers/:id',
            'Reviewer management UI in Developer Portal settings (invite, list, revoke, copy login link)',
          ],
          fixed: [],
          changed: [
            'Admin verification endpoints scope queries by developer_id for reviewer tokens',
            'Reviewers cannot use override decision (admin-only)',
          ],
        },
        {
          version: '1.6.0',
          date: '2026-03-26',
          added: [
            'Batch verification processing — full pipeline (OCR, cross-validation, quality gates) without live capture',
            'Admin status override with new_status field on PUT /api/admin/verification/:id/review',
            'Webhook forwarding on admin review actions (approve, reject, override)',
            'Verification Management page at /admin/verifications — stats, filterable table, detail view, review actions',
          ],
          fixed: [],
          changed: [
            'Batch items that fail quality gates correctly marked as failed with rejection reason',
          ],
        },
        {
          version: '1.5.0',
          date: '2026-03-24',
          added: [
            'First-run setup wizard (auto-detects zero-developer state)',
            'Mobile handoff link endpoint: PATCH /api/verify/handoff/:token/link',
            'Engine Worker microservice — standalone container for ML verification (OCR, face detection, liveness, deepfake)',
          ],
          fixed: [
            'Mobile handoff desktop notification — exponential backoff retry, 30-min session timeout',
          ],
          changed: [
            'Core API image reduced from ~2GB to ~250MB — ML dependencies isolated in engine container (~1.5GB)',
            'Service architecture: database + engine + api + frontend',
          ],
        },
        {
          version: '1.2.0',
          date: '2026-03-20',
          added: [],
          fixed: [],
          changed: [
            'Liveness system: removed dead MediaPipe code path, renamed MultiFrame → HeadTurn',
            'Malformed liveness_metadata now returns HTTP 400 (VALIDATION_ERROR) instead of silently falling back to passive mode',
            'Removed legacy multi_frame_color challenge type alias — only head_turn is accepted',
            'color_sequence field is now optional (clients no longer need to send it)',
          ],
        },
        {
          version: '1.1.0',
          date: '2026-03-19',
          added: [
            'Visual authenticity checks — FFT analysis, color distribution, zone validation, deepfake detection',
            'Webhook resend endpoint (POST /api/developer/webhooks/:id/deliveries/:did/resend)',
            'Per-API-key scoping for webhook endpoints',
            'Developer-configurable LLM fallback for OCR with date disambiguation',
            'Account deletion endpoint (DELETE /api/developer/account)',
            'Email OTP + GitHub OAuth authentication (replaced insecure password login)',
            'Webhook delivery logs endpoint (GET /api/developer/webhooks/:id/deliveries)',
            'Webhook test endpoint with timeout handling',
            'AML/sanctions screening opt-in addon',
            'US driver\'s license format validator',
            '/health endpoint for Railway health checks + /api/health for API consumers',
          ],
          fixed: [
            'NULL events column silently filtering out webhook deliveries',
            'Per-provider metrics now derived from results JSONB instead of session-level aggregates',
            'Missing webhook_deliveries table migration',
            'OTP security hardening — atomic verify, timing-safe comparison, fail-closed',
            'Trailing AAMVA field markers stripped from space-separated DLN',
            'CORS origins always include production domains',
          ],
          changed: [],
        },
        {
          version: '1.0.0',
          date: '2025-12-01',
          added: [
            'Initial release — document OCR, face matching, verification pipeline',
            'RESTful API for verification workflows',
            'API key management system',
            'Webhook notification system with HMAC-SHA256 signing',
            'Sandbox environment for developer testing',
            'Rate limiting and abuse protection',
          ],
          fixed: [],
          changed: [],
        },
      ].map(release => (
        <div key={release.version} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 0, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Pill color={C.cyan} bg={C.cyanDim}>v{release.version}</Pill>
            <span style={{ fontFamily: C.mono, fontSize: '0.78rem', color: C.dim }}>{release.date}</span>
            {release.version === '1.8.3' && <Pill color={C.green} bg={C.greenDim}>latest</Pill>}
          </div>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
            {release.added.length > 0 && (
              <div>
                <div style={{ fontFamily: C.mono, fontSize: '0.68rem', fontWeight: 600, color: C.green, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green }} />
                  Added
                </div>
                {release.added.map(item => (
                  <div key={item} style={{ fontFamily: C.sans, fontSize: '0.82rem', color: C.muted, lineHeight: 1.65, padding: '3px 0 3px 12px', borderLeft: `2px solid ${C.green}20` }}>
                    {item}
                  </div>
                ))}
              </div>
            )}
            {release.fixed.length > 0 && (
              <div>
                <div style={{ fontFamily: C.mono, fontSize: '0.68rem', fontWeight: 600, color: C.amber, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.amber }} />
                  Fixed
                </div>
                {release.fixed.map(item => (
                  <div key={item} style={{ fontFamily: C.sans, fontSize: '0.82rem', color: C.muted, lineHeight: 1.65, padding: '3px 0 3px 12px', borderLeft: `2px solid color-mix(in srgb, ${C.amber} 13%, transparent)` }}>
                    {item}
                  </div>
                ))}
              </div>
            )}
            {release.changed.length > 0 && (
              <div>
                <div style={{ fontFamily: C.mono, fontSize: '0.68rem', fontWeight: 600, color: C.blue, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.blue }} />
                  Changed
                </div>
                {release.changed.map(item => (
                  <div key={item} style={{ fontFamily: C.sans, fontSize: '0.82rem', color: C.muted, lineHeight: 1.65, padding: '3px 0 3px 12px', borderLeft: `2px solid color-mix(in srgb, ${C.blue} 13%, transparent)` }}>
                    {item}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}

      <Divider />

      {/* ══ SUPPORT ══════════════════════════════════════════════════════ */}
      <SectionAnchor id="support" />
      <H2 index="03">Support & Resources</H2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {[
          { icon: '🔑', title: 'Developer Portal', desc: 'Get API keys, view usage stats and analytics', href: '/developer', cta: 'Open Portal →' },
          { icon: '🔍', title: 'Review Dashboard', desc: 'Review, approve, and manage verifications with your team', href: '/docs/review', cta: 'View Guide →' },
          { icon: '🎮', title: 'Live Demo', desc: 'Try the full verification flow with a sandbox key', href: '/demo', cta: 'Open Demo →' },
          { icon: '📦', title: 'JavaScript SDK', desc: 'TypeScript SDK with real-time events and embed component', href: 'https://www.npmjs.com/package/@saflens/sdk', cta: 'npm install @saflens/sdk' },
          { icon: '🔧', title: 'GitHub', desc: 'Source code, examples, and issue tracker', href: 'https://github.com/team-saflens/saflens-community', cta: 'View on GitHub →' },
          { icon: '✉️', title: 'Email Support', desc: 'Technical support and integration help', href: mailto(SUPPORT_EMAIL), cta: SUPPORT_EMAIL },
        ].map(r => (
          <div key={r.title} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 0, padding: '20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: '1.4rem' }}>{r.icon}</div>
            <div style={{ fontFamily: C.sans, fontSize: '0.9rem', fontWeight: 600, color: C.text }}>{r.title}</div>
            <div style={{ fontFamily: C.sans, fontSize: '0.78rem', color: C.muted, lineHeight: 1.55, flex: 1 }}>{r.desc}</div>
            <a href={r.href} target={r.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" style={{ fontFamily: C.sans, fontSize: '0.8rem', fontWeight: 600, color: C.cyan, textDecoration: 'none' }}>{r.cta}</a>
          </div>
        ))}
      </div>

    </DocLayout>
  );
};
